import { useEffect, useState, useCallback } from "react";
import { Gem, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { inputCls, Label } from "./shared";

export function FragEventPanel() {
  const [ev, setEv] = useState(null);
  const [hours, setHours] = useState(24);
  const [mult, setMult] = useState(2);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => api.get("/admin/frag-event").then((r) => setEv(r.data)).catch(() => setEv({ active: false })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);

  const start = async () => {
    if (!(hours > 0)) return toast.error("Enter a duration greater than 0");
    setBusy(true);
    try { const r = await api.post("/admin/frag-event", { hours: Number(hours), multiplier: Number(mult) }); setEv(r.data); toast.success(`${mult}× fragment event started`); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/frag-event", { hours: 0 }); setEv(r.data); toast.success("Fragment event stopped"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  let remaining = "";
  if (ev?.active && ev.active_until) {
    const ms = new Date(ev.active_until).getTime() - now;
    if (ms > 0) {
      const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
      remaining = `${h}h ${m}m ${s}s`;
    }
  }

  return (
    <div data-testid="frag-event-panel" className={`mb-6 border p-4 rounded-sm ${ev?.active ? "border-amber-500/60 bg-amber-950/20" : "border-amber-900/30 bg-zinc-950"}`}>
      <div className="flex items-center gap-2 mb-3">
        <Gem size={16} className="text-amber-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Double Fragment Event</h3>
        {ev?.active && (
          <span data-testid="frag-event-active-badge" className="ml-auto flex items-center gap-1.5 text-xs text-amber-300 font-semibold">
            <Clock size={13} /> {ev.multiplier}× active · {remaining}
          </span>
        )}
      </div>
      {ev?.active ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-zinc-400 flex-1 min-w-[200px]">All managers earn <span className="text-amber-300 font-semibold">{ev.multiplier}×</span> salvage fragments until the timer ends.</p>
          <button data-testid="frag-event-stop-btn" onClick={stop} disabled={busy}
            className="px-4 py-2 border border-red-900/50 text-red-300 hover:border-red-500 text-sm rounded-sm disabled:opacity-50">End Event Now</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <p className="text-sm text-zinc-500 w-full mb-1">Boost fragment drop rates platform-wide for a limited time.</p>
          <div className="w-28"><Label>Duration (hours)</Label>
            <input data-testid="frag-event-hours" type="number" min="0.5" step="0.5" className={inputCls} value={hours} onChange={(e) => setHours(e.target.value)} /></div>
          <div className="w-28"><Label>Multiplier</Label>
            <select data-testid="frag-event-mult" className={inputCls} value={mult} onChange={(e) => setMult(Number(e.target.value))}>
              {[2, 3, 4, 5].map((m) => <option key={m} value={m}>{m}×</option>)}
            </select></div>
          <button data-testid="frag-event-start-btn" onClick={start} disabled={busy}
            className="px-5 py-2 bg-amber-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-amber-400 disabled:opacity-50 rounded-sm">Start Event</button>
        </div>
      )}
    </div>
  );
}
